import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, tokenStore } from "../lib/api";
import i18n from "../i18n";

const AuthCtx = createContext(null);

// When the user record carries a preferred_language, switch i18next to it
// — but only if the user hasn't already manually picked a different one in
// this browser session (manual choice wins until they log out + back in).
function applyUserLanguage(u) {
  const pref = u?.preferred_language;
  if (!pref) return;
  const stored = (typeof localStorage !== "undefined") && localStorage.getItem("i18nextLng");
  // Honour an explicit user-side override; otherwise sync from server.
  if (stored && stored !== pref) return;
  if (i18n.language !== pref) i18n.changeLanguage(pref);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);     // user object or null
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      applyUserLanguage(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    // Wipe any stale token before attempting login. iPad/Safari users who
    // had a previous session can end up with a token whose user no longer
    // exists (db flush, account rotation, role change) — the axios
    // interceptor would then attach that dead bearer to the login POST,
    // which is harmless for the call itself but causes a redirect loop
    // immediately after when /auth/me is queried with the stale token
    // still cached in a closure. Defensive clearing here makes login
    // self-healing.
    tokenStore.clear();
    const { data } = await api.post("/auth/login", { email, password });
    // Persist the bearer token so Safari/iPad (where the SameSite=None;Secure
    // cookie gets dropped by ITP inside our preview iframe) can still
    // authenticate subsequent requests via Authorization header.
    if (data?.access_token) tokenStore.set(data.access_token);
    // Refresh from /auth/me so the user carries computed flags (is_admin,
    // is_supplier) immediately — the login response omits them.
    await checkAuth();
    return data.user;
  };

  const register = async (payload) => {
    // returns { requires_verification: true, email } — does NOT auto-login.
    const { data } = await api.post("/auth/register", payload);
    return data;
  };

  const verifyOtp = async (email, otp) => {
    const { data } = await api.post("/auth/verify-otp", { email, otp });
    if (data?.access_token) tokenStore.set(data.access_token);
    await checkAuth();
    return data.user;
  };

  const resendOtp = async (email) => {
    await api.post("/auth/resend-otp", { email });
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    tokenStore.clear();
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, setUser, loading, login, register, verifyOtp, resendOtp, logout, checkAuth }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
